// 1. Define a type for the user object (customize with your actual user data)
import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import type { PayloadAction } from "@reduxjs/toolkit";
import { callApi } from "@/api/callApi";
import { API_ENDPOINT, API_URL } from "@/api/constant";
// import type { IUser } from "@/interface/employeeOnBoard";
import type { IUser } from "@/interface/user.model";
// import { IUser } from '@/interfaces/user.interface';

interface UserState {
  isHolidayLoading: boolean;
  isAuthenticated: boolean;
  user: IUser | null;
  role: string | null;
  isSideBarOpen: number;
  isActiveTab: string;
  userWebsiteId: string;
  loading: "idle" | "pending" | "succeeded" | "failed";
  userDetailsLoading: "idle" | "pending" | "succeeded" | "failed";
  isLoadingOrder: "idle" | "pending" | "succeeded" | "failed";
  error: string | null;
  notificationCount: number;
  scrollPosition: number;
}

const initialState: UserState = {
  isAuthenticated: false,
  isHolidayLoading: false,
  user: null,
  isSideBarOpen: 0,
  isActiveTab: "",
  userWebsiteId: "",
  loading: "idle",
  userDetailsLoading: "idle",
  isLoadingOrder: "idle",
  notificationCount: 0,
  error: null,
  role: null,
  scrollPosition: 0,
};

export const fetchNotificationCount = createAsyncThunk(
  "user/fetchNotificationCount",
  async (_, { rejectWithValue }) => {
    try {
      const response = await callApi(
        `${API_URL}${API_ENDPOINT.notification}/count`,
        { method: "GET" },
      );
      // console.log('fetchNotificationCount response', response);
      return response?.data?.count;
    } catch (error: any) {
      return rejectWithValue(error.message || "Failed to fetch notifications");
    }
  },
);

export const fetchUserDetails = createAsyncThunk(
  "user/fetchUserDetails",
  async (_, { rejectWithValue }) => {
    try {
      const response = await callApi("/user/profile", {
        method: "GET",
      });
      return response?.data || response;
    } catch (error: any) {
      return rejectWithValue(error.message || "Failed to fetch user details");
    }
  },
);

const userSlice = createSlice({
  name: "user",
  initialState,
  reducers: {
    setAuthenticated: (state, action: PayloadAction<boolean>) => {
      state.isAuthenticated = action.payload;
    },
    setUserData: (state, action: PayloadAction<IUser | null>) => {
      state.user = action.payload;
    },
    setUserWebsiteId: (state, action: PayloadAction<string>) => {
      state.userWebsiteId = action.payload;
    },
    setRole: (state, action: PayloadAction<string>) => {
      state.role = action.payload;
    },
    setNotificationCount: (state, action: PayloadAction<number>) => {
      state.notificationCount = action.payload;
    },
    setSideBarOpen: (state, action: PayloadAction<number>) => {
      state.isSideBarOpen = action.payload;
    },

    setActiveTab: (state, action: PayloadAction<string>) => {
      state.isActiveTab = action.payload;
    },
    logout: (state) => {
      Object.assign(state, initialState);
      state.isAuthenticated = false;
    },
    saveSideBarScrollPosition: (state, action: PayloadAction<number>) => {
      state.scrollPosition = action.payload;
    },
  },

  extraReducers: (builder) => {
    builder
      .addCase(fetchNotificationCount.pending, (state) => {
        state.loading = "pending";
        state.error = null;
      })
      .addCase(
        fetchNotificationCount.fulfilled,
        (state, action: PayloadAction<number>) => {
          state.loading = "succeeded";
          state.notificationCount = action.payload;
        },
      )
      .addCase(fetchNotificationCount.rejected, (state, action) => {
        state.loading = "failed";
        state.error = action.payload as string;
      })
      .addCase(fetchUserDetails.pending, (state) => {
        state.userDetailsLoading = "pending";
      })
      .addCase(
        fetchUserDetails.fulfilled,
        (state, action: PayloadAction<IUser>) => {
          state.userDetailsLoading = "succeeded";
          state.user = { ...state.user, ...action.payload };
        },
      )
      .addCase(fetchUserDetails.rejected, (state, action) => {
        state.userDetailsLoading = "failed";
        state.error = action.payload as string;
      });
  },
});

export const {
  setAuthenticated,
  setUserData,
  setSideBarOpen,
  setActiveTab,
  logout,
  setUserWebsiteId,
  setRole,
  setNotificationCount,
  saveSideBarScrollPosition,
} = userSlice.actions;

export default userSlice.reducer;
